import { useEffect, useRef } from 'react';

import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useTableContext } from '@/contexts/table-context';

import type { OpeningInfo } from '../types';
import type { SelectOpeningRow } from './columns';

/**
 * Keeps job-posting selection in sync with the table checkboxes, including
 * rows selected on other pages. Current-page rows stay cached so checking a
 * box can write the opening title immediately — the rail must not wait on a
 * later fetch.
 */
export function useSelectOpeningsSelection(tableRows: SelectOpeningRow[]) {
  const { rowSelection } = useTableContext();
  const { setSelectedOpeningIds, setOpenings } = useJobPostingContext();
  const selectedRowsCacheRef = useRef<Map<string, SelectOpeningRow>>(new Map());

  useEffect(() => {
    const selectedIds = Object.keys(rowSelection);
    const selectedIdSet = new Set(selectedIds);
    const cache = selectedRowsCacheRef.current;
    const pageIdSet = new Set(tableRows.map(row => row.external_id));

    tableRows.forEach(row => {
      cache.set(row.external_id, row);
    });

    Array.from(cache.keys()).forEach(id => {
      if (!selectedIdSet.has(id) && !pageIdSet.has(id)) cache.delete(id);
    });

    setSelectedOpeningIds(selectedIds);
    setOpenings(prev => {
      const next: Record<string, OpeningInfo> = {};
      for (const id of selectedIds) {
        const cached = cache.get(id);
        const previous = prev[id];
        next[id] = {
          externalId: id,
          ...(cached?.title
            ? { title: cached.title }
            : previous?.title
              ? { title: previous.title }
              : {}),
        };
      }
      return next;
    });
  }, [rowSelection, setOpenings, setSelectedOpeningIds, tableRows]);
}
